import { useShowContext } from 'framework/show';
import {
  WASTE_REPORTING_METHOD,
  WASTE_REPORTING_METHOD_OPTIONS,
  WASTE_TYPE,
  WASTE_TYPE_OPTIONS,
  WasteHeader,
  RedmineIssueListField,
} from 'domain/shared/waste';
import WasteBreadcrumbs from 'domain/projects/newProject/shared/breadcrumbs/WasteBreadcrumbs';
import { WASTE_STATUS, WASTE_STATUS_OPTIONS } from 'domain/shared/newProject';
import { ShowPageLayout } from 'framework/ui/layout';
import { ShowSection, SectionRow, FieldGroup, LabeledFieldLayout, BigSectionRow } from 'framework/ui/layout';
import { DateField, EnumField, TextField } from 'framework/ui/field';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { LedgerLink } from 'domain/shared/ledger';
import ProjectField from 'infrastructure/integrations/redmine/ProjectField';
import { Typography } from '@mui/material';

const ViewWasteContent = () => {
  const data = useShowContext()?.data;

  const redmineIssueIds = data?.getValueBySource('laborWasteDetails.ticketsIds') as number[];
  const isLaborCostWaste = data?.getValueBySource('type') === WASTE_TYPE.LABOR_COST_WASTE;
  const isReportingMethodByTicketAndEmployee =
    data?.getValueBySource('laborWasteDetails.reportingMethod') === WASTE_REPORTING_METHOD.BY_EMPLOYEE_AND_TICKET_IDS;
  const isReportingMethodByTicket =
    data?.getValueBySource('laborWasteDetails.reportingMethod') === WASTE_REPORTING_METHOD.BY_TICKET_IDS;

  return (
    <ShowPageLayout entityHeader={<WasteHeader />} breadcrumbs={<WasteBreadcrumbs />} headerButtons={[]}>
      <ShowSection title="Main information">
        <SectionRow>
          <EnumField label="Type" source="type" options={WASTE_TYPE_OPTIONS} />
          <EnumField
            isVisible={isLaborCostWaste}
            label="Reporting Method"
            source="laborWasteDetails.reportingMethod"
            options={WASTE_REPORTING_METHOD_OPTIONS}
          />
          <EnumField label="Status" source="status" options={WASTE_STATUS_OPTIONS} />
          {data?.rejectionDetails?.rejectReason ? (
            <TextField
              label="Rejection reason"
              source="rejectionDetails.rejectReason"
              value={<Typography color="error">{data.rejectionDetails.rejectReason}</Typography>}
            />
          ) : null}
        </SectionRow>

        <SectionRow>
          <InvoiceTotalField
            label="Amount"
            source="otherWasteDetails.cost.amount"
            currencySource="otherWasteDetails.cost.currency"
            isVisible={!isLaborCostWaste}
          />
          <DateField isVisible={!isLaborCostWaste} label="Issue Date" source="otherWasteDetails.issueDate" />

          <EmployeeLinkFieldWithNameLoading
            label="Worker"
            source="laborWasteDetails.employeeAlias"
            isVisible={isLaborCostWaste && !isReportingMethodByTicket}
          />

          <FieldGroup
            isVisible={(isReportingMethodByTicket || isReportingMethodByTicketAndEmployee) && isLaborCostWaste}
          >
            <LabeledFieldLayout
              value={redmineIssueIds?.map((issueId: number) => {
                return <RedmineIssueListField key={issueId} id={issueId} />;
              })}
              label="Redmine ticket ID (s)"
            />
          </FieldGroup>

          <DateField
            isVisible={!isReportingMethodByTicket && !isReportingMethodByTicketAndEmployee && isLaborCostWaste}
            label="Period from"
            source="laborWasteDetails.period.startDate"
          />
          <DateField
            isVisible={!isReportingMethodByTicket && !isReportingMethodByTicketAndEmployee && isLaborCostWaste}
            label="Period to"
            source="laborWasteDetails.period.endDate"
          />
          <ProjectField
            isVisible={!isReportingMethodByTicket && !isReportingMethodByTicketAndEmployee && isLaborCostWaste}
            source="laborWasteDetails.projectId"
            nameSource="laborWasteDetails.projectId"
            label="Project"
          />
        </SectionRow>

        <BigSectionRow>
          <TextField label="Waste reason" source="reason" />
        </BigSectionRow>
      </ShowSection>

      <ShowSection title="Approval details" isVisible={data.getValueBySource('status') === WASTE_STATUS.APPROVED}>
        <SectionRow>
          <LedgerLink
            source="approvalDetails.attribution.sourceAccountId"
            nameSource="sourceAccountId"
            label="Source Account"
          />
          <TextField
            source="approvalDetails.attribution.sourceBudgetCategory"
            nameSource="sourceBudgetCategory"
            label="Source Budget Category"
          />
          <LedgerLink
            source="approvalDetails.attribution.targetAccountId"
            nameSource="targetAccountId"
            label="Target Account"
          />
          <TextField
            source="approvalDetails.attribution.targetBudgetCategory"
            nameSource="targetBudgetCategory"
            label="Target Budget Category"
          />
          <EmployeeLinkFieldWithNameLoading label="Approved By" source="approvalDetails.approvedBy" />
        </SectionRow>
      </ShowSection>
    </ShowPageLayout>
  );
};

export default ViewWasteContent;
